import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeFirestore, persistentLocalCache, persistentSingleTabManager, memoryLocalCache } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID
};

export const OPERATIONS_DB_ID = `${import.meta.env.VITE_OPERATIONS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`;

let firestoreInitialized = false;
let emulatorsConnected = false;
let appCheckInitialized = false;

export let appCheckInstance = null;

/** @param {import('firebase/app').FirebaseApp} app */
function initDefaultFirestore(app) {
  const defaultDbSettings = {
    localCache: memoryLocalCache(),
  };
  try {
    initializeFirestore(app, defaultDbSettings);
  } catch (err) {
    console.warn('Default Firestore already initialized:', err);
  }
}

/** @param {import('firebase/app').FirebaseApp} app */
function initOperationsFirestore(app) {
  const operationsDbSettings = {
    localCache: import.meta.env.NODE_ENV === 'production'
      ? persistentLocalCache({ tabManager: persistentSingleTabManager({}) })
      : memoryLocalCache(),
  };
  try {
    initializeFirestore(app, operationsDbSettings, OPERATIONS_DB_ID);
  } catch (err) {
    console.warn('Operations Firestore already initialized:', err);
  }
}

/**
 * Initialize Firebase app.
 *
 * Options:
 *  - explicit `useEmulators` argument (boolean)
 *  - defaults to `NODE_ENV === 'development'`
 *
 * Call `initFirebase({ useEmulators: false })` to force real Firebase in dev.
 */
export function initFirebase(options = {}) {
  const { useEmulators } = options || {};
  const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

  if (!firestoreInitialized) {
    initDefaultFirestore(app);
    initOperationsFirestore(app);
    firestoreInitialized = true;
  }

  if (!appCheckInitialized) {
    const siteKey =
      typeof import.meta.env.VITE_FIREBASE_APPCHECK_RECAPTCHA_KEY === 'string'
        ? import.meta.env.VITE_FIREBASE_APPCHECK_RECAPTCHA_KEY.trim()
        : '';

    if (siteKey.length > 0) {
      appCheckInstance = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } else {
      console.warn('No App Check reCAPTCHA key configured. App Check will not be initialized.');
    }

    appCheckInitialized = true;
  }

  const shouldUseEmulators = typeof useEmulators === 'boolean'
    ? useEmulators
    : import.meta.env.NODE_ENV === 'development';

  if (shouldUseEmulators && !emulatorsConnected) {
    try {
      const auth = getAuth(app);
      const functions = getFunctions(app);
      connectAuthEmulator(auth, 'http://localhost:9099');
      connectFunctionsEmulator(functions, 'localhost', 5001);

      emulatorsConnected = true;
    } catch (err) {
      console.warn('Failed to connect Firebase emulators:', err);
    }
  }

  return app;
}

export default initFirebase;
